import { ReactComponent as ReloadIcon } from 'assett/icons/reload.svg';
import { ReactComponent as SearchHideIcon } from 'assett/icons/search_hide_icon.svg';
import { ReactComponent as SearchIcon } from 'assett/icons/search_icon.svg';
import MultipleSelect from 'components/commons/form/MultipleSelect';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import HeaderCellWithSorting from 'components/commons/list/HeaderCellWithSorting';
import { Dayjs } from 'dayjs';
import {
	handleChange,
	resetInputFilters,
	setSearch,
} from 'feature/form/formSlice';
import { setDownloadAll } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Checkbox, IconButton, TableCell, TableHead, TableRow } from 'styles';
import CustomDateRangePicker from 'styles/datePicker/CustomDateRangePicker';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

const BillingTableHeader = () => {
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { search } = useSelector((store: RootState) => store.form);

	const dispatch = useDispatch<AppDispatch>();
	const {
		claimsBillingStatuses,
		adminBillingStatuses,
		tabSelected,
		billingTypologies,
	} = useSelector((store: RootState) => store.billing);

	const { downloadAll } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);

	const { claimClients } = useSelector((store: RootState) => store.form);

	const {
		inputBillingTypologies,
		inputBillingStatus,
		inputClaimStartDate,
		inputClaimEndDate,
		inputClaimBillingStartDate,
		inputClaimBillingEndDate,
		inputClaimSettlementStartDate,
		inputClaimSettlementEndDate,
		inputBillingCarriers,
		inputBillingMarkets,
		inputClients,
		inputGroups,
		inputClaimStatus,
		inputBillingNetworkStatus,
		claimNetworkStatuses,
		claimsStatuses,
		carriers,
		groups,
		networks,
	} = useSelector((store: RootState) => store.form);

	const billingStatuses =
		tabSelected === 'claims' ? claimsBillingStatuses : adminBillingStatuses;

	const handleCheckDownload = () => {
		dispatch(setDownloadAll(!downloadAll));
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell>
					<Checkbox
						label=""
						onClick={handleCheckDownload}
						checked={downloadAll}
						sx={{ p: 0 }}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="billingId" widthBox={210} widthInput={210} />
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="billingTypologies"
						widthBox={300}
						widthInput={300}
					>
						<MultipleSelect
							items={billingTypologies.map(el => ({
								code: el.code,
								name: tDbLabels(el.label),
							}))}
							name={'billingTypologies'}
							value={inputBillingTypologies}
							onChange={e =>
								dispatch(handleChange({ name: 'inputBillingTypologies', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="billingStatusCode"
						widthBox={210}
						widthInput={210}
					>
						<SelectGeneric
							action={status =>
								dispatch(handleChange({ name: 'inputBillingStatus', value: status }))
							}
							items={billingStatuses.map(el => ({
								value: el.code,
								text: tDbLabels(el.label),
							}))}
							name={'billing'}
							initialValue={inputBillingStatus}
							sxForm={{ flex: 1, width: '100%' }}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="carrierCodes" widthBox={210} widthInput={210}>
						<MultipleSelect
							items={carriers}
							name={'carrier'}
							value={inputBillingCarriers}
							onChange={e =>
								dispatch(handleChange({ name: 'inputBillingCarriers', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="clientCodes" widthBox={210} widthInput={210}>
						<MultipleSelect
							items={claimClients}
							name={'client'}
							value={inputClients}
							onChange={e =>
								dispatch(handleChange({ name: 'inputClients', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="groupCodes" widthBox={210} widthInput={210}>
						<MultipleSelect
							items={groups}
							name={'group'}
							value={inputGroups}
							onChange={e => dispatch(handleChange({ name: 'inputGroups', value: e }))}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="externalClaimId"
						widthBox={210}
						widthInput={210}
						primarySorting="claimDate"
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="carrierClaimId"
						widthBox={210}
						widthInput={210}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="claimDate" widthBox={270} widthInput={270}>
						<CustomDateRangePicker
							name="claimDate"
							dates={[inputClaimStartDate, inputClaimEndDate]}
							setStartDate={e =>
								dispatch(
									handleChange({ name: 'inputClaimStartDate', value: e as Dayjs }),
								)
							}
							setEndDate={e =>
								dispatch(handleChange({ name: 'inputClaimEndDate', value: e as Dayjs }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="billingDate" widthBox={270} widthInput={270}>
						<CustomDateRangePicker
							name="billingDate"
							dates={[inputClaimBillingStartDate, inputClaimBillingEndDate]}
							setStartDate={e =>
								dispatch(
									handleChange({
										name: 'inputClaimBillingStartDate',
										value: e as Dayjs,
									}),
								)
							}
							setEndDate={e =>
								dispatch(
									handleChange({ name: 'inputClaimBillingEndDate', value: e as Dayjs }),
								)
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="settlementDate"
						widthBox={270}
						widthInput={270}
					>
						<CustomDateRangePicker
							name="settlementDate"
							dates={[inputClaimSettlementStartDate, inputClaimSettlementEndDate]}
							setStartDate={e =>
								dispatch(
									handleChange({
										name: 'inputClaimSettlementStartDate',
										value: e as Dayjs,
									}),
								)
							}
							setEndDate={e =>
								dispatch(
									handleChange({
										name: 'inputClaimSettlementEndDate',
										value: e as Dayjs,
									}),
								)
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="claimStatus" widthBox={210} widthInput={210}>
						<SelectGeneric
							action={status =>
								dispatch(
									handleChange({
										name: 'inputClaimStatus',
										value: status === 'All' ? '' : status,
									}),
								)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All') },
								...claimsStatuses.map(el => ({
									value: el.name,
									text: tDbLabels(el.label),
								})),
							]}
							name={'claimStatus'}
							initialValue={inputClaimStatus || 'All'}
							sxForm={{ flex: 1, width: '100%' }}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="inNetwork" widthBox={210} widthInput={210}>
						<SelectGeneric
							action={status =>
								dispatch(
									handleChange({
										name: 'inputBillingNetworkStatus',
										value: status === 'All' ? '' : status,
									}),
								)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All') },
								...claimNetworkStatuses.map(el => ({
									value: el.name,
									text: tDbLabels(el.label),
								})),
							]}
							name={'claimNetworkStatus'}
							initialValue={inputBillingNetworkStatus || 'All'}
							sxForm={{ flex: 1, width: '100%' }}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="networkCodes" widthBox={230} widthInput={230}>
						<MultipleSelect
							items={networks.map(el => ({
								code: el.networkCode,
								name: el.name,
							}))}
							name={'billingMarket'}
							value={inputBillingMarkets}
							onChange={e =>
								dispatch(handleChange({ name: 'inputBillingMarkets', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ minWidth: '56px', textAlign: 'center' }}>
					{search && (
						<IconButton onClick={() => dispatch(resetInputFilters())}>
							<ReloadIcon />
						</IconButton>
					)}
				</TableCell>
				<TableCell>
					<IconButton onClick={() => dispatch(setSearch(!search))}>
						{search ? <SearchHideIcon /> : <SearchIcon />}
					</IconButton>
				</TableCell>
			</TableRow>
		</TableHead>
	);
};

export default BillingTableHeader;
